// frontend/src/pages/LoginPage.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaSignInAlt } from 'react-icons/fa';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const { login, isAuthenticated, userInfo } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && userInfo) {
      if (userInfo.role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/user/dashboard');
      }
    }
  }, [isAuthenticated, userInfo, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    try {
      await login(email, password);
    } catch (err) {
      console.error("Giriş Hatası:", err);
      let errorMessage = 'Bir hata oluştu.';
      if (err.response) {
          errorMessage = err.response.data.message || 'Giriş bilgileri hatalı.';
      } else if (err.request) {
          errorMessage = 'Sunucuya ulaşılamıyor. Bağlantınızı kontrol edin.';
      } else {
          errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0f111a] relative overflow-hidden font-sans">
      
      {/* --- ARKA PLAN IŞIKLARI (KIRMIZI & TURUNCU) --- */}
      {/* Sol Üst: Kırmızı */}
      <div className="absolute -top-20 -left-20 w-96 h-96 bg-red-600/20 rounded-full blur-[100px] animate-pulse"></div>
      {/* Sağ Alt: Turuncu */}
      <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-orange-600/10 rounded-full blur-[100px] animate-pulse delay-1000"></div>

      {/* --- ANA KART (GLASSMORPHISM) --- */}
      <div className="relative z-10 w-full max-w-md p-8 mx-4">
        
        {/* Kart Gövdesi */}
        <div className="bg-gray-900/60 backdrop-blur-xl border border-gray-700/50 rounded-3xl shadow-2xl p-8 transform transition-all hover:border-red-500/20">
          
          {/* Logo Alanı */}
          <div className="flex flex-col items-center mb-8">
            <div className="w-24 h-24 bg-gradient-to-b from-gray-800 to-gray-900 rounded-2xl flex items-center justify-center shadow-lg border border-gray-700/50 mb-4 p-4">
               <img src="/logo.png" alt="Payda Logo" className="w-full h-full object-contain filter drop-shadow-lg" />
            </div>
            <h2 className="text-2xl font-bold text-white tracking-wide">
              Ortak <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">PAYDA</span> Noktası
            </h2>
            <p className="text-gray-400 text-sm mt-2">Hesabınıza erişmek için giriş yapın.</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            
            {/* E-Posta Input */}
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <FaEnvelope className="text-gray-500 group-focus-within:text-red-500 transition-colors" />
              </div>
              <input
                type="email"
                className="w-full bg-gray-800/50 border border-gray-700 text-white text-sm rounded-xl focus:ring-2 focus:ring-red-500/50 focus:border-red-500 block w-full pl-11 p-3.5 outline-none transition-all placeholder-gray-500"
                placeholder="E-posta adresiniz"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            {/* Şifre Input */}
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <FaLock className="text-gray-500 group-focus-within:text-red-500 transition-colors" />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                className="w-full bg-gray-800/50 border border-gray-700 text-white text-sm rounded-xl focus:ring-2 focus:ring-red-500/50 focus:border-red-500 block w-full pl-11 pr-10 p-3.5 outline-none transition-all placeholder-gray-500"
                placeholder="Şifreniz"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-white transition-colors focus:outline-none"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            {/* Hata Mesajı */}
            {error && (
               <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400 text-sm animate-shake">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {error}
               </div>
            )}

            {/* Giriş Butonu (Kırmızıdan Turuncuya Gradient) */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center gap-2 text-white bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 focus:ring-4 focus:ring-red-900 font-bold rounded-xl text-sm px-5 py-3.5 text-center transition-all transform active:scale-[0.98] shadow-lg shadow-red-600/20 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Giriş Yapılıyor...
                </>
              ) : (
                <>
                  <FaSignInAlt /> Giriş Yap
                </>
              )}
            </button>
          </form>

        </div>
        
        {/* Footer */}
        <p className="text-center text-gray-600 text-xs mt-8">
            © 2025 aps.<a href="https://www.urartech.com" target="_blank" title="URARTECH Web Yazılım">URARTECH</a> | Güvenli Giriş Sistemi
        </p>
      </div>
    </div>
  );
};

export default LoginPage;